import {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
  startTransition,
} from 'react'
import {
  AcademyEvent,
  AcademyVerified,
} from '../../Api/Events/Academy/academyEvents.types'
import {
  fetchAcademyVerified,
  fetchActiveAcademyEvents,
} from '../../Api/Events/Academy/events.api'
import { SelectListItem } from '../../Components/Layout/Selectors/General/GeneralSelect'
import { getFullNameByCode } from '../../Utils'
import { STORAGE_NAMES } from '../../Constant'
import { StorageContext } from '../../Context/StorageContext'
import {
  AcademyFilterObject,
  AcademyHookReturns,
  ParticipantType,
  participantTypes,
} from './academy.types'
import { getSearchParams, getCurrentPath } from '../../Utils'

export const usePlayData = (): AcademyHookReturns => {
  const { isStorageReady, fetchAndCache } = useContext(StorageContext)!
  const [academyEvents, setAcademyEvents] = useState<AcademyEvent[]>([])
  const [filteredList, setFilteredList] = useState<AcademyEvent[]>([])
  const [participantSelect, setParticipantSelect] = useState<ParticipantType>(
    participantTypes[0]
  )
  const [monthSelect, setMonthSelect] = useState<string>('By Month')
  const [stateSelect, setStateSelect] = useState<string>('All Locations')
  const [trainingSelect, setTrainingSelect] = useState<string>('All Training')
  const [stateList, setStateList] = useState<SelectListItem[]>([])
  const [monthList, setMonthList] = useState<SelectListItem[]>([])
  const [trainingList, setTrainingList] = useState<SelectListItem[]>([])
  const [verifiedClubs, setVerifiedClubs] = useState<AcademyVerified[]>([])
  const urlFilterApplied = useRef<boolean>(false)

  const [loading, setLoading] = useState<boolean>(true)
  const [verifiedLoading, setVerifiedLoading] = useState<boolean>(true)

  useEffect(() => {
    if (isStorageReady) {
      fetchAcademyData()
    }
  }, [isStorageReady])

  useEffect(() => {
    const urlParams = getSearchParams()
    const locationParam = urlParams?.get('location')
    const monthParam = urlParams?.get('month')
    const trainingParam = urlParams?.get('training')

    if (
      (locationParam || monthParam || trainingParam) &&
      !urlFilterApplied.current &&
      academyEvents.length > 0 &&
      stateList.length > 1 &&
      monthList.length > 1 &&
      trainingList.length > 1
    ) {
      applyFiltersFromUrl(stateList, monthList, trainingList)
    }
  }, [academyEvents, stateList, monthList, trainingList])

  const createDropdowns = useCallback(async (acEvs: AcademyEvent[]) => {
    if (acEvs.length > 0) {
      const locations = createStateList(acEvs)
      setStateList(locations)
      const months = createMonthList(acEvs)
      setMonthList(months)
      const trainings = createTrainingList(acEvs)
      setTrainingList(trainings)
    }
  }, [])

  const filterEvents = useCallback(
    (participant: string, state: string, month: string, training: string) => {
      const filtered = academyEvents.filter(
        (ev) =>
          (participant === 'all'
            ? ev?.boysOrGirls !== ''
            : ev.boysOrGirls.toLowerCase() === participant ||
              ev.boysOrGirls === 'Both') &&
          (state === '' || state === 'All Locations'
            ? ev.state !== ''
            : getFullNameByCode(ev.state) === state) &&
          (month === '' || month === 'By Month'
            ? ev.formattedEventDate !== '' && ev.formattedEventDate !== null
            : ev.formattedEventDate.slice(0, 3) === month) &&
          (training === '' || training === 'All Training'
            ? ev.eventType !== ''
            : ev.eventType === training)
      )
      setFilteredList(filtered)
      return filtered
    },
    [academyEvents, participantSelect, stateSelect, monthSelect, trainingSelect]
  )

  const findMatchingState = (
    locationParam: string,
    states: SelectListItem[]
  ): SelectListItem | undefined => {
    const normalizedLocation = locationParam.toLowerCase()
    if (states.length === 0) return undefined
    return states.find((s) => s.label.toLowerCase() === normalizedLocation)
  }

  const findMatchingMonth = (
    monthParam: string,
    months: SelectListItem[]
  ): SelectListItem | undefined => {
    if (!monthParam || months.length === 0) return undefined
    const normalizedMonthParam = monthParam.toLowerCase()
    return months.find(
      (monthItem) => monthItem.label.toLowerCase() === normalizedMonthParam
    )
  }

  const findMatchingTraining = (
    trainingParam: string,
    trainings: SelectListItem[]
  ): SelectListItem | undefined => {
    if (!trainingParam || trainings.length === 0) return undefined
    const normalizedTrainingParam = trainingParam.toLowerCase()
    return trainings.find(
      (trainingItem) => trainingItem.label.toLowerCase() === normalizedTrainingParam
    )
  }

  const applyFiltersFromUrl = useCallback(
    (
      states: SelectListItem[],
      months: SelectListItem[],
      trainings: SelectListItem[]
    ): void => {
      try {
        const urlParams = getSearchParams()
        const locationParam = urlParams?.get('location')
        const monthParam = urlParams?.get('month')
        const trainingParam = urlParams?.get('training')
        if (!locationParam && !monthParam && !trainingParam) return

        let newSelectedState = stateSelect
        let newSelectedMonth = monthSelect
        let newSelectedTraining = trainingSelect

        let locationParamProcessed = false
        if (locationParam && states.length > 0) {
          const matchingState = findMatchingState(locationParam, states)
          if (matchingState) {
            newSelectedState = matchingState.label
            locationParamProcessed = true
          }
        }

        let monthParamProcessed = false
        if (monthParam && months.length > 0) {
          const matchingMonth = findMatchingMonth(monthParam, months)
          if (matchingMonth) {
            newSelectedMonth = matchingMonth.label
            monthParamProcessed = true
          }
        }

        let trainingParamProcessed = false
        if (trainingParam && trainings.length > 0) {
          const matchingTraining = findMatchingTraining(trainingParam, trainings)
          if (matchingTraining) {
            newSelectedTraining = matchingTraining.label
            trainingParamProcessed = true
          }
        }

        if (locationParamProcessed || monthParamProcessed || trainingParamProcessed) {
          if (newSelectedState !== stateSelect) {
            setStateSelect(newSelectedState)
          }
          if (newSelectedMonth !== monthSelect) {
            setMonthSelect(newSelectedMonth)
          }
          if (newSelectedTraining !== trainingSelect) {
            setTrainingSelect(newSelectedTraining)
          }
          filterEvents(
            participantSelect.value,
            newSelectedState,
            newSelectedMonth,
            newSelectedTraining
          )
          urlFilterApplied.current = true
        }
      } catch (error) {
        console.error('Error applying filters from URL:', error)
      }
    },
    [academyEvents, stateList, monthList, trainingList]
  )

  const getAcademyEvents = useCallback(async () => {
    try {
      const acEvents = await fetchActiveAcademyEvents()
      if (acEvents && acEvents.length > 0) {
        let sorted = acEvents.sort(
          (a, b) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime()
        )
        return sorted
      }
      return []
    } catch (error) {
      console.error('Error fetching academy events:', error)
      return []
    }
  }, [])

  const getVerifiedClubs = useCallback(async () => {
    try {
      const clubs = await fetchAcademyVerified()
      if (clubs && clubs.length > 0) {
        let sorted = clubs.sort((a, b) => (a.location > b.location ? 1 : -1))
        return sorted
      }
      return []
    } catch (error) {
      console.error('Error fetching verified clubs:', error)
      return []
    }
  }, [])



  const fetchAcademyData = useCallback(async () => {
    try {
      startTransition(() => {
        setLoading(true)
        setVerifiedLoading(true)
      })

      const fetcher = async (): Promise<{
        events: AcademyEvent[]
        verifiedClubs: AcademyVerified[]
      }> => {
        const [events, clubs] = await Promise.all([
          getAcademyEvents(),
          getVerifiedClubs(),
        ])
        return {
          events: events || [],
          verifiedClubs: clubs || [],
        }
      }

      const onUpdate = (data: {
        events: AcademyEvent[]
        verifiedClubs: AcademyVerified[]
      }) => {
        if (data?.events) {
          setAcademyEvents(data.events)
          setFilteredList(data.events)
          createDropdowns(data.events)
          setLoading(false)
        }

        if (data?.verifiedClubs) {
          setVerifiedClubs(data.verifiedClubs)
          setVerifiedLoading(false)
        }
      }

      await fetchAndCache<{ events: AcademyEvent[]; verifiedClubs: AcademyVerified[] }>(
        STORAGE_NAMES.pll_play,
        fetcher,
        onUpdate
      )

      startTransition(() => {
        setLoading(false)
        setVerifiedLoading(false)
      })
    } catch (error) {
      console.error('Error fetching academy data:', error)
      startTransition(() => {
        setLoading(false)
        setVerifiedLoading(false)
      })
    }
  }, [])

  const getGameWeekendEvents = useCallback(
    (market: string) => {
      if (!academyEvents || academyEvents.length === 0) return
      const gameEv = academyEvents.find(
        (ev) => ev.gameWeekendEvent && ev.market && ev.market.toLowerCase() === market
      )
      return gameEv
    },
    [academyEvents]
  )

  const createStateList = (events: AcademyEvent[]) => {
    const stateDropdown = events
      .reduce((accumulator: SelectListItem[], event) => {
        const stateLabel = getFullNameByCode(event.state) || event.state
        if (!accumulator.some((s) => s.label === stateLabel)) {
          accumulator.push({ label: stateLabel, value: event.state })
        }
        return accumulator
      }, [])
      .sort((a, b) => (a.label > b.label ? 1 : -1))
    return [{ label: 'All Locations', value: 'All Locations' }, ...stateDropdown]
  }

  const createMonthList = (events: AcademyEvent[]) => {
    const l = events.reduce(
      (accumulator: SelectListItem[], event) => {
        if (!event.formattedEventDate) {
          return accumulator
        }
        const monthLabel = event.formattedEventDate.slice(0, 3)
        if (!accumulator.some((mo) => mo.label === monthLabel)) {
          accumulator.push({ label: monthLabel, value: monthLabel })
        }
        return accumulator
      },
      [{ label: 'By Month', value: 'By Month' }] // Initial value
    )
    return l
  }

  const createTrainingList = (events: AcademyEvent[]) => {
    const l = events.reduce(
      (accumulator: SelectListItem[], event) => {
        if (!event.formattedEventDate) {
          return accumulator
        }
        const trainingLabel = event.eventType
        if (trainingLabel && !accumulator.some((tr) => tr.label === trainingLabel)) {
          accumulator.push({ label: trainingLabel, value: trainingLabel })
        }
        return accumulator
      },
      [{ label: 'All Training', value: 'All Training' }] // Initial value
    )
    return l
  }

  const updateUrlWithFilters = useCallback(
    (participant: string, state: string, month: string, training: string) => {
      const searchParams = new URLSearchParams()
      if (participant && participant !== 'all') {
        searchParams.set('participant', participant)
      }
      if (state && state !== 'All Locations') {
        searchParams.set('location', state)
      }
      if (month && month !== 'By Month') {
        searchParams.set('month', month)
      }
      if (training && training !== 'All Training') {
        searchParams.set('training', training)
      }
      const newUrl = searchParams.toString()
        ? `${getCurrentPath()}?${searchParams.toString()}`
        : getCurrentPath()
      window.history.replaceState({}, '', newUrl)
    },
    []
  )

  const handleFilterChange = useCallback(
    (filterType: string, value: string) => {
      let currentParticipant = participantSelect.value
      let currentState = stateSelect
      let currentMonth = monthSelect
      let currentTraining = trainingSelect

      switch (filterType) {
        case 'participant':
          const foundParticipant = participantTypes.find((type) => type.value === value)
          if (value === 'all') {
            setParticipantSelect(participantTypes[0])
            currentParticipant = 'all'
          } else if (foundParticipant) {
            setParticipantSelect(foundParticipant)
            currentParticipant = foundParticipant.value
          }
          break
        case 'state':
          setStateSelect(value)
          currentState = value
          break
        case 'month':
          setMonthSelect(value)
          currentMonth = value
          break
        case 'training':
          setTrainingSelect(value)
          currentTraining = value
          break
        default:
          break
      }
      filterEvents(currentParticipant, currentState, currentMonth, currentTraining)
      updateUrlWithFilters(
        currentParticipant,
        currentState,
        currentMonth,
        currentTraining
      )
    },
    [participantSelect, stateSelect, monthSelect, trainingSelect]
  )

  const returnFilteredEvents = useCallback((filterObj: AcademyFilterObject) => {
    return filterEvents(
      filterObj?.participant || 'all',
      filterObj?.state || 'All Locations',
      filterObj?.month || 'By Month',
      filterObj?.training || 'All Training'
    )
  }, [])

  const handleReset = useCallback(() => {
    const defaultParticipant = participantTypes[0]
    setStateSelect('All Locations')
    setMonthSelect('By Month')
    setTrainingSelect('All Training')
    setParticipantSelect(defaultParticipant)

    filterEvents(defaultParticipant.value, 'All Locations', 'By Month', 'All Training')
    updateUrlWithFilters(
      defaultParticipant.value,
      'All Locations',
      'By Month',
      'All Training'
    )
  }, [participantSelect, stateSelect, monthSelect, trainingSelect])

  return {
    academyEvents,
    verifiedClubs,
    filteredList,
    stateList,
    stateSelect,
    monthList,
    monthSelect,
    trainingList,
    trainingSelect,
    participantSelect,
    loading,
    verifiedLoading,
    handleFilterChange,
    handleReset,
    getGameWeekendEvents,
    returnFilteredEvents,
  }
}
